import { useRef } from "react";
import { App, TableColumnsType, TableProps } from "antd";
import type { SortOrder } from "antd/es/table/interface";
import { useLocation, useNavigate } from "react-router-dom";
import VLTable, { VLTableProps } from "views/uikit/VLTable";
import {
  ValidationCatalog,
  ModelCatalogSortField,
  SortOrder as APISortOrder,
} from "vl-api/vlSchemas";
import { useValidationJobsTable } from "./useValidationJobsTable";
import TableFileIcon from "assets/icons/TableFileIcon";
import styles from "./style.module.scss";
import TextWithTooltip from "views/components/TextWithTooltip";
import VLToastComponent, {
  VLToastComponentProps,
} from "views/components/VLToastComponent";
import { toast } from "react-toastify";
import { TableFiltersProps } from "views/uikit/VLTable/TableFilters/TableFilters";
import TrashIcon from "assets/icons/TrashIcon";
import classNames from "classnames";

import useValidationFilters, {
  updateURLWithFilters,
} from "./useValidationFilter";
import { useGetValidationCatalogFilterOptionsApiV1ModelsCatalogValidationsFiltersGet } from "../../../../vl-api/vlComponents";

const ValidationJobsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toastIdRef = useRef<any>(null);
  const { modal } = App.useApp();

  const { filters: currentFilters, queryParams: apiQueryParams } =
    useValidationFilters(location.search);

  // Get filter options from backend API
  const filterOptionsResponse =
    useGetValidationCatalogFilterOptionsApiV1ModelsCatalogValidationsFiltersGet(
      {},
    );

  const filterOptions = filterOptionsResponse?.data;

  // Generate available filters using backend data
  const availableFilters = useValidationFilters(
    location.search,
    filterOptions,
  ).availableFilters;

  const { tableData, loading, handleDelete, isDeleting, totalItemsCount } =
    useValidationJobsTable(apiQueryParams);

  const handleTableFilterChange = async (filters: any) => {
    updateURLWithFilters(navigate, filters);
  };

  // Get current sort state from URL params
  const currentSortBy = apiQueryParams.sort_by as
    | ModelCatalogSortField
    | undefined;
  const currentSortOrder = apiQueryParams.sort_order as
    | APISortOrder
    | undefined;

  const handleSortChange: TableProps<ValidationCatalog>["onChange"] = (
    _pagination,
    _filters,
    sorter,
  ) => {
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortBy = sorter.columnKey as ModelCatalogSortField;
      let sortOrder: APISortOrder;

      // Check if clicking on the same column that's already sorted
      if (currentSortBy === sortBy) {
        // Toggle between asc and desc
        sortOrder = currentSortOrder === "asc" ? "desc" : "asc";
      } else {
        // New column clicked - default to desc
        sortOrder = "desc";
      }

      updateURLWithFilters(
        navigate,
        {
          ...apiQueryParams,
          sort_by: sortBy,
          sort_order: sortOrder,
        },
        false,
      );
    } else if (!sorter || (Array.isArray(sorter) && sorter.length === 0)) {
      // Reset to default sort
      const { sort_by, sort_order, ...restParams } = apiQueryParams;
      updateURLWithFilters(
        navigate,
        {
          ...restParams,
          sort_by: "model_name" as ModelCatalogSortField,
          sort_order: "desc" as APISortOrder,
        },
        false,
      );
    }
  };

  // Helper function to get sortOrder for a column
  const getSortOrder = (
    columnKey: ModelCatalogSortField,
  ): SortOrder | undefined => {
    if (currentSortBy === columnKey && currentSortOrder) {
      return currentSortOrder === "asc" ? "ascend" : "descend";
    }
    return undefined;
  };

  const handleReportClick = (report_path?: string | null) => {
    if (!!report_path) {
      window.open(report_path, "_blank");
    }
  };

  const showErrorToast = (title: string, description?: string) => {
    const toastProps: VLToastComponentProps = {
      title,
      status: "ERROR" as const,
      description: description || "",
    };

    if (toastIdRef.current) {
      toast.update(toastIdRef.current, {
        render: <VLToastComponent {...toastProps} />,
        type: toast.TYPE.ERROR,
        autoClose: 5000,
      });
    } else {
      toastIdRef.current = toast.error(<VLToastComponent {...toastProps} />, {
        autoClose: 5000,
      });
    }
  };

  const handleDeleteValidationJob = (validationId: string) => {
    modal.confirm({
      title: "Delete Validation Job",
      content: (
        <>
          <p>Are you sure you want to delete this validation job?</p>
          <span>
            This will permanently remove it from the VL catalog. It will not
            delete any files from disk.
          </span>
        </>
      ),
      width: 560,
      okText: "Yes, delete",
      okButtonProps: { danger: true },
      closable: true,
      onOk: async () => {
        try {
          await handleDelete(validationId);
        } catch (err: any) {
          showErrorToast(
            "Oops! Something went wrong!",
            err ?? "Failed to delete validation job.",
          );
        }
      },
    });
  };

  const allColumns: (TableColumnsType<ValidationCatalog>[number] & {
    show?: boolean;
  })[] = [
    {
      title: "Model Name",
      dataIndex: "model_name",
      key: "model_name",
      width: 200,
      render: (text: string) => <TextWithTooltip text={text} />,
      show: true,
      sorter: true,
      sortOrder: getSortOrder("model_name"),
    },
    {
      title: "Dataset Name",
      dataIndex: "dataset_name",
      key: "dataset_name",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text} />,
      show: true,
      sorter: true,
      sortOrder: getSortOrder("dataset_name"),
    },
    {
      title: "Dataset ID",
      dataIndex: "dataset_id",
      key: "dataset_id",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text} />,
      show: true,
    },
    {
      title: "Job",
      key: "job_name",
      width: 114,
      render: (_, record) => {
        const val = (record.metadata as any)?.["Job Name"] || "-";
        return <TextWithTooltip text={val} />;
      },
      show: true,
    },
    {
      title: "Setup",
      key: "setup_name",
      width: 114,
      render: (_, record) => {
        const val = (record.metadata as any)?.["Setup Name"] || "-";
        return <TextWithTooltip text={val} />;
      },
      show: true,
    },
    {
      title: "Recipe list",
      key: "recipes",
      width: 114,
      render: (_, record) => {
        const recipes = (record.metadata as any)?.["Recipes Name"];
        const val = Array.isArray(recipes) ? recipes.join(", ") : "-";
        return <TextWithTooltip text={val} />;
      },
      show: true,
    },
    {
      title: "Tool list",
      key: "tool_list",
      width: 114,
      render: (_, record) => {
        const tools = (record.metadata as any)?.["Tool List"];
        const val = Array.isArray(tools) ? tools.join(", ") : "-";
        return <TextWithTooltip text={val} />;
      },
      show: true,
    },
    {
      title: "GT Source",
      dataIndex: "gt_source",
      key: "gt_source",
      width: 114,
      render: (text: string) => <TextWithTooltip text={text || "N/A"} />,
      show: true,
    },
    {
      title: "Results",
      key: "results",
      width: 72,
      render: (_, record: ValidationCatalog) => {
        const hasReport = !!record.report_path || !!record.results_path;
        const resultsIconClass = classNames(styles.resultIcon, {
          [styles.disabled]: !hasReport,
        });
        return (
          <div
            className={resultsIconClass}
            onClick={() =>
              hasReport &&
              handleReportClick(record.report_path || record.results_path)
            }
          >
            <TableFileIcon />
          </div>
        );
      },
      show: true,
    },
  ];

  const visibleColumns = allColumns.filter((col) => col.show);

  const actionsConfig = {
    items: (record: ValidationCatalog) => [
      {
        key: "delete",
        label: "Delete",
        icon: <TrashIcon />,
        onClick: () => handleDeleteValidationJob(record.id),
        disabled: isDeleting,
        danger: true,
        className: styles.actionItems,
      },
    ],
  };

  const filtersConfig: TableFiltersProps = {
    availableFilters,
    filters: currentFilters,
    onTableFilterChange: handleTableFilterChange,
  };

  const tableProps: VLTableProps<ValidationCatalog> = {
    columns: visibleColumns,
    dataSource: tableData,
    loading: loading,
    rowKey: "id",
    actions: actionsConfig,
    tableFilters: filtersConfig,
    className: styles.modelTable,
    onSortChange: handleSortChange,
    pagination: {
      total: totalItemsCount || 0,
      tooltip: "",
    },
    localeEmptyText: loading ? "Loading..." : "No validation jobs found",
  };

  return (
    <div className={styles.modelTableContainer}>
      <VLTable<ValidationCatalog> {...tableProps} />
    </div>
  );
};

export default ValidationJobsTable;
